// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "../../../theme/SwiperOverrides.css";

import React, { useState } from "react";
// import Swiper core and required modules
import { A11y, Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "../../../components/Pagination/Pagination";
import SignPostLinks, {
	type SignPostLinksProps,
} from "../../../components/SignPostCards/SignPostLinks/SignPostLinks";
import { useClientSide } from "../../../hooks/useClientSide";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";
import * as styles from "./ParagraphPopularTopics.module.scss";

export interface ParagraphPopularTopicsProps {
	heading: string;
	popTopics: SignPostLinksProps[];
}

export const getParagraphPopularTopicsProps = ({
	field_section_title,
	relationships: { field_popular_topic },
}: Queries.ParagraphPopularTopicsDataFragment): ParagraphPopularTopicsProps => ({
	heading: field_section_title,
	popTopics: field_popular_topic?.map((popTopic) => ({
		titleLink: {
			title: popTopic.field_parent_link.title,
			url: popTopic.field_parent_link.uri,
		},
		childLinks: popTopic.field_child_links.map((childLink) => ({
			title: childLink.title,
			url: childLink.uri,
		})),
	})),
});
export const ParagraphPopularTopics: React.FC<ParagraphPopularTopicsProps> = ({
	heading,
	popTopics,
}) => {
	const [swiper, setSwiper] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const prevSlide = () => {
		swiper?.slidePrev();
		setCurrentSlide((curr) => curr - 1);
	};
	const nextSlide = () => {
		swiper?.slideNext();
		setCurrentSlide((curr) => curr + 1);
	};
	const windowWidth = useWindowWidth();
	const isClientSide = useClientSide();

	const getSlideCount = (windowWidth: number) => {
		switch (true) {
			case windowWidth >= 900:
				return 3;
			case windowWidth >= 600:
				return 2;
			default:
				return 1;
		}
	};

	return (
		<WrappedSection
			sectionName="ParagraphPopularTopics"
			sectionColour={Colours.WHITE}
		>
			<div className={styles.wrapperinner}>
				{heading?.length > 0 && <h3 className={styles.title}>{heading}</h3>}

				<div className={styles.swiperwrapper}>
					{isClientSide && (
						<Swiper
							className={styles.swiper}
							modules={[Virtual, Navigation, A11y]}
							noSwiping={true}
							initialSlide={0}
							preventClicks={false}
							spaceBetween={16}
							preventClicksPropagation={false}
							onSwiper={setSwiper}
							breakpoints={{
								320: {
									slidesPerView: getSlideCount(320),
								},
								600: {
									slidesPerView: getSlideCount(600),
								},
								900: {
									slidesPerView: getSlideCount(900),
								},
							}}
						>
							{popTopics?.map((popTopic) => (
								<SwiperSlide key={popTopic.titleLink.title}>
									<SignPostLinks {...popTopic} />
								</SwiperSlide>
							))}
						</Swiper>
					)}
				</div>
				<div className={styles.pagination}>
					<Pagination
						key={windowWidth}
						position={currentSlide}
						total={popTopics.length}
						onFirstClick={() => {
							swiper?.slideTo(0);
							setCurrentSlide(0);
						}}
						onLastClick={() => {
							swiper?.slideTo(popTopics.length - 1);
							setCurrentSlide(popTopics.length - 1);
						}}
						onNextClick={nextSlide}
						onPreviousClick={prevSlide}
						previousDisabled={currentSlide === 0}
						firstDisabled={currentSlide === 0}
						nextDisabled={
							currentSlide >= popTopics.length - getSlideCount(windowWidth)
						}
						lastDisabled={
							currentSlide >= popTopics.length - getSlideCount(windowWidth)
						}
						slim={true}
					/>
				</div>
			</div>
		</WrappedSection>
	);
};

export default ParagraphPopularTopics;
